/* ============================================================
   OXS Audio EU — PDP buy box interactions
   Gallery thumbnail swap · quantity stepper · variant selector.
   Add-to-cart itself is handled by Dawn's <product-form> + product-form.js.
   ============================================================ */
(() => {
  const root = document.querySelector('[data-oxs-pdp]');
  if (!root) return;

  /* ---- Gallery: thumbnail → main image swap ---- */
  const mainImg = root.querySelector('[data-pdp-main-img]');
  const thumbs = root.querySelectorAll('[data-pdp-thumb]');
  const swapMain = (src, srcset) => {
    if (!mainImg || !src) return;
    mainImg.classList.add('is-swap');
    setTimeout(() => {
      delete mainImg.dataset.hires;
      mainImg.src = src;
      if (srcset) mainImg.srcset = srcset;
      mainImg.classList.remove('is-swap');
    }, 180);
  };
  thumbs.forEach((btn) => {
    btn.addEventListener('click', () => {
      swapMain(btn.dataset.img, btn.dataset.srcset);
      thumbs.forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
    });
  });

  /* ---- Gallery: normalize product fill ----
     Source shots fill their canvas very differently (A2/G2 tight, soundbars sparse).
     Measure the real product bounding box on a tiny canvas (alpha first, corner-colour
     fallback) and scale the image so every product fills ~86% of the frame. */
  const mainBox = root.querySelector('[data-pdp-zoom]');
  const fitCache = {};
  const applyFit = () => {
    if (!mainImg) return;
    const src = mainImg.currentSrc || mainImg.src;
    if (fitCache[src] !== undefined) { mainImg.style.setProperty('--fit', fitCache[src]); return; }
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      let scale = 1;
      try {
        const S = 64;
        const c = document.createElement('canvas');
        c.width = S; c.height = S;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, S, S);
        const d = ctx.getImageData(0, 0, S, S).data;
        const corner = [d[0], d[1], d[2], d[3]];
        const hasAlpha = corner[3] < 250;
        let minX = S, minY = S, maxX = -1, maxY = -1;
        for (let y = 0; y < S; y++) {
          for (let x = 0; x < S; x++) {
            const i = (y * S + x) * 4;
            const isProduct = hasAlpha
              ? d[i + 3] > 30
              : (Math.abs(d[i] - corner[0]) + Math.abs(d[i + 1] - corner[1]) + Math.abs(d[i + 2] - corner[2])) > 48;
            if (isProduct) {
              if (x < minX) minX = x; if (x > maxX) maxX = x;
              if (y < minY) minY = y; if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX >= minX) {
          const fill = Math.max((maxX - minX + 1) / S, (maxY - minY + 1) / S);
          scale = Math.min(1.9, Math.max(1, 0.86 / fill));
        }
      } catch (e) { /* CORS-tainted or decode issue → leave at 1 */ }
      fitCache[src] = scale.toFixed(3);
      mainImg.style.setProperty('--fit', fitCache[src]);
    };
    probe.onerror = () => { fitCache[src] = 1; };
    probe.src = src;
  };
  if (mainImg) {
    if (mainImg.complete) applyFit();
    mainImg.addEventListener('load', applyFit);
  }

  /* ---- Gallery: zoom (desktop: hover-pan · touch: tap toggles) ---- */
  if (mainBox && mainImg) {
    const ZOOM = 2.2;
    /* Zoomed magnification is ~2.8x effective — the 1100px responsive pick pixelates.
       On zoom start, silently swap in the 2048px CDN rendition once it has loaded. */
    const upscale = () => {
      const cur = mainImg.currentSrc || mainImg.src;
      if (!cur || mainImg.dataset.hires) return;
      const hi = cur.replace(/width=\d+/, 'width=2048');
      if (hi === cur) { mainImg.dataset.hires = '1'; return; }
      const pre = new Image();
      pre.onload = () => { mainImg.srcset = ''; mainImg.src = hi; mainImg.dataset.hires = '1'; };
      pre.src = hi;
    };
    const setOrigin = (e) => {
      const r = mainBox.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      mainImg.style.transformOrigin = `${x}% ${y}%`;
    };
    const zoomOff = () => {
      mainBox.classList.remove('is-panning', 'is-zoomed');
      mainImg.style.setProperty('--zoom', 1);
      mainImg.style.transformOrigin = '50% 50%';
    };
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (fine) {
      mainBox.addEventListener('mouseenter', (e) => {
        upscale();
        setOrigin(e);
        mainBox.classList.add('is-panning', 'is-zoomed');
        mainImg.style.setProperty('--zoom', ZOOM);
      });
      mainBox.addEventListener('mousemove', setOrigin);
      mainBox.addEventListener('mouseleave', zoomOff);
    } else {
      mainBox.addEventListener('click', (e) => {
        if (mainBox.classList.contains('is-zoomed')) { zoomOff(); return; }
        upscale();
        setOrigin(e);
        mainBox.classList.add('is-zoomed');
        mainImg.style.setProperty('--zoom', ZOOM);
      });
    }
  }

  /* ---- Quantity stepper ---- */
  const qtyInput = root.querySelector('[data-pdp-qty]');
  const qtyMinus = root.querySelector('[data-pdp-qty-minus]');
  const qtyPlus = root.querySelector('[data-pdp-qty-plus]');
  if (qtyInput && qtyMinus && qtyPlus) {
    const clamp = (v) => Math.max(parseInt(qtyInput.min || '1', 10), Math.min(parseInt(qtyInput.max || '99', 10), v));
    qtyMinus.addEventListener('click', () => { qtyInput.value = clamp((parseInt(qtyInput.value, 10) || 1) - 1); });
    qtyPlus.addEventListener('click', () => { qtyInput.value = clamp((parseInt(qtyInput.value, 10) || 1) + 1); });
  }

  /* ---- Variant selector ---- */
  const variantBtns = root.querySelectorAll('[data-pdp-variant]');
  const hiddenId = root.querySelector('.product-variant-id');
  const priceNow = root.querySelector('[data-pdp-price-now]');
  const ctaText = root.querySelector('[data-pdp-cta-text]');
  const submitBtn = root.querySelector('[type="submit"]');
  const variantName = root.querySelector('#pdpVariantName');

  variantBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.dataset.available === 'false') return;
      variantBtns.forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-checked', 'false'); });
      btn.classList.add('is-active');
      btn.setAttribute('aria-checked', 'true');

      if (variantName && btn.dataset.name) variantName.textContent = btn.dataset.name;
      if (hiddenId) hiddenId.value = btn.dataset.id;
      if (priceNow && btn.dataset.price) priceNow.textContent = btn.dataset.price;
      if (ctaText && btn.dataset.price) ctaText.textContent = btn.dataset.price;
      if (submitBtn) {
        const avail = btn.dataset.available !== 'false';
        submitBtn.disabled = !avail;
        const span = submitBtn.querySelector('span');
        if (span) span.textContent = avail ? (submitBtn.dataset.addLabel || 'Add to Cart') : (submitBtn.dataset.soldLabel || 'Sold out');
      }
      /* Per-variant colour gallery: show only the selected colour's thumbs,
         jump the main image to that group's first thumb. Falls back to the
         variant's data-img when media aren't colour-grouped. */
      const color = btn.dataset.color;
      let firstThumb = null;
      if (color) {
        thumbs.forEach((t) => {
          const match = (t.dataset.color || '') === color;
          t.classList.toggle('is-hidden-variant', !match);
          if (match && !firstThumb) firstThumb = t;
        });
      }
      if (firstThumb) {
        thumbs.forEach((t) => t.classList.remove('is-active'));
        firstThumb.classList.add('is-active');
        swapMain(firstThumb.dataset.img, firstThumb.dataset.srcset);
      } else if (btn.dataset.img) {
        swapMain(btn.dataset.img, btn.dataset.srcset);
        thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.img === btn.dataset.img));
      }
    });
  });
})();
